import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Pagination from "@mui/material/Pagination";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ProgressBar from "../../../../UI/ProgressBar";
import {
  ADMIN_ACTIVITY_LOG_PAGE_SIZE,
  useGetAdminActivityLogsQuery,
  useLazyGetAdminActivityLogByIdQuery,
} from "../../../../../redux-toolkit/features/adminActivityLog/adminActivityLogApiSlice";
import { Error404 } from "../../../../UI/404/Error404";
import { SortIcon } from "../../../Manage Organization/Employee/EmployeeTable";
import emptyStateSearch from "../../../../../assets/employee/empty_state_search.svg";
import { ReportsTruncatedName } from "../../ReportsTruncatedName";

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const formatActionLabel = (action, fallback = "") => {
  const fromAction = String(action || "")
    .split(".")
    .filter(Boolean)
    .map((part) => part.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase()))
    .join(" ");
  const raw = fromAction || String(fallback || "");
  return raw
    .replace(/\u2013|\u2014/g, " ")
    .replace(/\s*[-–—]\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const ActivityLogRow = ({ row, index, check, actionLabels }) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [fetchDetail, { isFetching }] = useLazyGetAdminActivityLogByIdQuery();

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail) {
      try {
        const result = await fetchDetail(row._id).unwrap();
        setDetail(result?.data ?? null);
      } catch {
        setDetail(null);
      }
    }
  };

  return (
    <>
      <TableRow hover>
        {check.rowNum && <TableCell align="center">{index + 1}</TableCell>}
        {check.createdAt && (
          <TableCell align="center">{formatDateTime(row.createdAt)}</TableCell>
        )}
        {check.actor && (
          <TableCell align="center">
            <div>{row.actorName || "—"}</div>
            <div style={{ fontSize: 12, color: "var(--grey500, #666)" }}>
              {row.actorEmail || ""}
            </div>
          </TableCell>
        )}
        {check.role && <TableCell align="center">{row.actorRole || "—"}</TableCell>}
        {check.action && (
          <TableCell align="center">
            {formatActionLabel(row.action, actionLabels[row.action]) || "—"}
          </TableCell>
        )}
        {check.entityType && (
          <TableCell align="center">{row.entityType || "—"}</TableCell>
        )}
        {check.entity && (
          <TableCell align="center">{row.entityLabel || "—"}</TableCell>
        )}
        {check.company && (
          <TableCell align="center">
            <ReportsTruncatedName value={row.companyName} />
          </TableCell>
        )}
        <TableCell align="center">
          <IconButton size="small" onClick={handleToggle} aria-label="details">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={Object.values(check).filter(Boolean).length + 1}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1, padding: 2, background: "var(--grey50, #fafafa)" }}>
              {isFetching && !detail ? (
                <ProgressBar />
              ) : (
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontSize: 13,
                  }}
                >
                  {JSON.stringify(
                    {
                      changes: detail?.changes ?? null,
                      metadata: detail?.metadata ?? null,
                      route: detail?.route || row.route,
                      method: detail?.httpMethod || row.httpMethod,
                    },
                    null,
                    2
                  )}
                </pre>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const AdminActivityLogTable = ({
  check,
  searchQuery = "",
  startDate,
  endDate,
  actionFilter = [],
  entityTypeFilter = [],
  actionLabels = {},
}) => {
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortType, setSortType] = useState("desc");
  const [page, setPage] = useState(1);

  const filterArgs = useMemo(
    () => ({
      sortBy,
      type: sortType,
      query: searchQuery,
      startDate,
      endDate,
      action:
        Array.isArray(actionFilter) && actionFilter.length
          ? actionFilter
          : undefined,
      entityType:
        Array.isArray(entityTypeFilter) && entityTypeFilter.length
          ? entityTypeFilter
          : undefined,
    }),
    [sortBy, sortType, searchQuery, startDate, endDate, actionFilter, entityTypeFilter]
  );

  useEffect(() => {
    setPage(1);
  }, [sortBy, sortType, searchQuery, startDate, endDate, actionFilter, entityTypeFilter]);

  const { data, isError, isLoading, isFetching } = useGetAdminActivityLogsQuery(
    {
      ...filterArgs,
      page,
      limit: ADMIN_ACTIVITY_LOG_PAGE_SIZE,
    },
    { refetchOnMountOrArgChange: true }
  );

  const pagination = data?.pagination;
  const rows = data?.data ?? [];
  const totalPages = pagination?.totalPages ?? 0;

  const handleSorting = (sortKey) => {
    if (sortBy === sortKey) {
      setSortType((current) => (current === "desc" ? "asc" : "desc"));
      return;
    }
    setSortBy(sortKey);
    setSortType("asc");
  };

  if (isLoading || isFetching) {
    return (
      <div className="employee__loader">
        <ProgressBar />
      </div>
    );
  }

  if (isError) {
    return <Error404 />;
  }

  const allFieldsDeselected = Object.values(check).every((v) => !v);
  const showPagination = !allFieldsDeselected && totalPages > 0 && rows.length > 0;

  return (
    <div style={{ width: "100%" }}>
      <TableContainer
        sx={{
          height: allFieldsDeselected ? "auto" : "100%",
          maxHeight: allFieldsDeselected ? "none" : "calc(100vh - 320px)",
          padding: "0 1px",
          width: "100%",
          maxWidth: "100%",
          overflowX: allFieldsDeselected ? "hidden" : "auto",
          overflowY: allFieldsDeselected ? "visible" : "auto",
        }}
      >
        <Table
          stickyHeader
          aria-label="activity logs table"
          className={check.rowNum ? "reports-table--indexed" : undefined}
          sx={allFieldsDeselected ? undefined : { minWidth: 1200 }}
        >
          <TableHead>
            <TableRow className="tableUi__head">
              {allFieldsDeselected ? (
                <TableCell align="center">Select fields to view data</TableCell>
              ) : (
                <>
                  {check.rowNum && <TableCell align="center">#</TableCell>}
                  {check.createdAt && (
                    <TableCell align="center" onClick={() => handleSorting("createdAt")}>
                      <div className="tableUi__head_sortable">
                        Date/Time <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.actor && (
                    <TableCell align="center" onClick={() => handleSorting("actorName")}>
                      <div className="tableUi__head_sortable">
                        Actor <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.role && (
                    <TableCell align="center" onClick={() => handleSorting("actorRole")}>
                      <div className="tableUi__head_sortable">
                        Role <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.action && (
                    <TableCell align="center" onClick={() => handleSorting("action")}>
                      <div className="tableUi__head_sortable">
                        Action <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.entityType && (
                    <TableCell align="center" onClick={() => handleSorting("entityType")}>
                      <div className="tableUi__head_sortable">
                        Entity Type <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.entity && (
                    <TableCell align="center" onClick={() => handleSorting("entityLabel")}>
                      <div className="tableUi__head_sortable">
                        Entity <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.company && (
                    <TableCell align="center" onClick={() => handleSorting("companyName")}>
                      <div className="tableUi__head_sortable">
                        Company <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  <TableCell align="center">Details</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {!allFieldsDeselected && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Box sx={{ py: 6 }}>
                    <img src={emptyStateSearch} alt="No activity logs" width={160} />
                    <div style={{ marginTop: 12, color: "var(--grey500, #666)" }}>
                      No activity logs found
                    </div>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {!allFieldsDeselected &&
              rows.map((row, index) => (
                <ActivityLogRow
                  key={row._id || index}
                  row={row}
                  index={(page - 1) * ADMIN_ACTIVITY_LOG_PAGE_SIZE + index}
                  check={check}
                  actionLabels={actionLabels}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {showPagination && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            padding: "15px 0",
          }}
        >
          <Pagination
            count={totalPages}
            shape="rounded"
            page={page}
            onChange={(_e, newPage) => {
              setPage(newPage);
            }}
          />
        </div>
      )}
    </div>
  );
};
